import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { apiClient } from '../services/apiClient';
import { showToast } from '../store/toaster/action';
import { UpdateCompanyServiceType } from '../helpers/types';

// Mirrors `companiesQueryKey` in `queries/lookups.ts` - duplicated (rather
// than imported) to avoid a circular import between the two query modules,
// since `lookups.ts` imports the relocated `getCompanyApi` from this file.
const lookupsCompaniesQueryKey = ['lookups', 'companies'];

// ---------------------------------------------------------------------------
// API functions (WHEEL-341e: relocated from the deleted src/store/company/
// slice). Only endpoints with a live (routed) consumer were ported - the V1
// CompaniesList/CompanyDetails page tree (src/pages/CompaniesList/CompaniesList.tsx,
// src/pages/CompanyDetails/CompanyDetails.tsx and most of its Tabs) was never
// wired into a <Route> in App.tsx and was deleted as dead code rather than
// converted; see the WHEEL-341e report for the full list.
// ---------------------------------------------------------------------------

/** Backs the shared `useCompaniesQuery` lookup in `queries/lookups.ts`. */
export const getCompanyApi = async (payload: string) => {
  const config = {
    method: 'GET',
    url: `company/list`,
    payload,
  };
  return apiClient(config);
};

/** Backs the shared `useServicesQuery` lookup in `queries/lookups.ts`. */
export const getServiceApi = async (payload: string) => {
  const config = {
    method: 'GET',
    url: `v2/assets/services`,
    payload,
  };
  return apiClient(config);
};

/** Backs the shared `useSourcesQuery` lookup in `queries/lookups.ts`. */
export const getSourcesApi = async (payload: string) => {
  const config = {
    method: 'GET',
    url: `v2/assets/threats/source`,
    payload,
  };
  return apiClient(config);
};

export interface CompanyListV1Params {
  d: string;
  u: string;
  s: string;
  h: string;
  ipp: number;
  pn: number;
  r?: string;
  m?: string;
  c?: number | string;
}

export const getCompanyListApi = async (payload: CompanyListV1Params) => {
  const { d, u, s, h, ipp, pn, r, m, c } = payload;
  const queryParameters = new URLSearchParams({
    d,
    u,
    s,
    h,
    ipp: String(ipp),
    pn: String(pn),
    ...(r && { r }),
    ...(m && { m }),
    ...(c !== undefined && { c: String(c) }),
  }).toString();

  const config = {
    method: 'GET',
    url: `company/all?${queryParameters}`,
    payload,
  };
  return apiClient(config);
};

export interface AddCompanyV1Payload {
  name: string;
  vat: string;
  address: string;
  zip: string;
  phone: string;
  email: string;
  logo: string;
}

export const addCompanyApi = async (data: AddCompanyV1Payload) => {
  const config = {
    method: 'POST',
    url: `company/add`,
    data,
  };
  return apiClient(config);
};

export const unsetParentCompanyApi = async (data: { companyId: string }) => {
  const config = {
    method: 'POST',
    url: `company/unsetparent`,
    data,
  };
  return apiClient(config);
};

/** Still called directly (no saga ever wrapped this endpoint) from
 * InformationTabV2 to remove one service from a V2 company. */
export const removeCompanyServiceApi = async (
  data: UpdateCompanyServiceType,
) => {
  const config = {
    method: 'POST',
    url: `company/removeservice`,
    data,
  };
  return apiClient(config);
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export interface CompanyListV1Item {
  Id: number;
  Name: string;
  Status?: string;
  Services?: { Id: number; Name: string }[];
  Created: string;
  /** Nested ancestor chain returned by `company/all?c=<id>` — the flat
   * Companies list already contains every descendant, so only Parent.Id is
   * needed to rebuild the tree (WHEEL-372). */
  Parent?: { Id: number; Name: string };
}

export interface CompanyListV1Response {
  Total: number;
  Companies: CompanyListV1Item[];
}

export const companyListQueryKeyPrefix = ['company', 'list'] as const;

export const companyListQueryKey = (params: CompanyListV1Params) => [
  ...companyListQueryKeyPrefix,
  params,
];

/** Paginated/searchable child-companies list (WHEEL-341e). Replaces the
 * `company.getCompanyList` slice cell - the only live (routed) consumer is
 * ChildCompaniesTab, reached via CompanyDetailsV2 -> CompanyDetailsTabsV2. */
export const useCompanyListQuery = (params: CompanyListV1Params) =>
  useQuery<CompanyListV1Response>({
    queryKey: companyListQueryKey(params),
    queryFn: async () => {
      const response = await getCompanyListApi(params);
      return response?.data ?? { Total: 0, Companies: [] };
    },
  });

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/**
 * Adds a V1 company (AddCompanyModal, reused from CompaniesListV2). Replaces
 * addCompanySaga's fan-out: rather than select()-ing the current list/company
 * caches and prepending the new row into them, this invalidates the V1
 * company-list query key AND the shared `useCompaniesQuery` lookup cache
 * (`['lookups','companies']`) so every consumer refetches fresh data - the
 * recipe recorded on WHEEL-341.
 */
export const useAddCompanyMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (payload: AddCompanyV1Payload) => {
      const response = await addCompanyApi(payload);
      return response?.data;
    },
    onSuccess: () => {
      dispatch(showToast('Company Added successfully.', 'success'));
      queryClient.invalidateQueries({ queryKey: companyListQueryKeyPrefix });
      queryClient.invalidateQueries({ queryKey: lookupsCompaniesQueryKey });
    },
    onError: (error: any) => {
      dispatch(showToast(error?.message || 'Failed to add company', 'error'));
    },
  });
};

/**
 * Detaches a child company from its parent (ChildCompaniesTab). Replaces
 * unsetParentCompanySaga's refetch-and-re-put of the child list with an
 * invalidation of the same query key.
 */
export const useUnsetParentCompanyMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (payload: { companyId: string; parentId?: string }) => {
      const response = await unsetParentCompanyApi(payload);
      return response?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companyListQueryKeyPrefix });
    },
    onError: () => {
      dispatch(showToast('Something went wrong', 'error'));
    },
  });
};
